import { type ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';
import type { Creator } from '@/types';
import { AnimateHeight } from '@/components/ui/animate-height';
import { CreatorAdminActions } from './creator-admin-actions';
import { BodyView, type DetailMode } from './creator-detail-sections';
import { ModeSection } from './creator-detail-mode-section';
import {
  anyActionVisible,
  computeFlags,
  type ActionFlags,
} from './creator-detail-flags';

type DetailProps = {
  creator: Creator;
  mode: DetailMode;
  setMode: (m: DetailMode) => void;
  onClose: () => void;
};

function AdminActions({
  setMode,
  flags,
}: {
  setMode: (m: DetailMode) => void;
  flags: ActionFlags;
}): ReactNode {
  return (
    <CreatorAdminActions
      onEdit={() => setMode('edit')}
      onDelete={() => setMode('delete')}
      onSanction={() => setMode('sanction')}
      onAlert={() => setMode('alert')}
      onRestore={() => setMode('restore')}
      onHardDelete={() => setMode('hard-delete')}
      canEdit={flags.canEdit}
      canDelete={flags.canDelete}
      canSanction={flags.canSanction}
      canAlert={flags.canAlert}
      canRestore={flags.canRestore}
      canHardDelete={flags.canHardDelete}
    />
  );
}

export function DetailBody(p: DetailProps): ReactNode {
  const { user, isAdmin, isCollector } = useAuth();
  const isOwner = !!user && p.creator.collector === user.id;
  const flags = computeFlags({
    creator: p.creator,
    isAdmin,
    isCollector,
    isOwner,
  });
  const showActions = p.mode === 'view' && anyActionVisible(flags);
  return (
    <AnimateHeight deps={p.mode}>
      <BodyView creator={p.creator} mode={p.mode} set={p.setMode} />
      <ModeSection {...p} />
      {showActions && <AdminActions setMode={p.setMode} flags={flags} />}
    </AnimateHeight>
  );
}
